export const UUID_LENGTH = 36
export const UUID_REGEX =
    /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/

export type KnovEmbedTarget = {
    type: 'quest' | 'answer' | 'share' | 'anonymous_share'
    id: string
    questId?: string
    answerId?: string
}

function getUrlPathSegments(url: string): string[] {
    if (!url || typeof url !== 'string') return []

    try {
        return new URL(url).pathname.replace(/\/+$/, '').split('/').filter(Boolean)
    } catch (e) {
        return url.split('?')[0].split('#')[0].replace(/\/+$/, '').split('/').filter(Boolean)
    }
}

function normalizeQuestPathId(value: string | null | undefined): string | null {
    if (!value) return null
    return value.match(UUID_REGEX)?.[0] || value
}

export function extractKnovEmbedTarget(url: string): KnovEmbedTarget | null {
    const segments = getUrlPathSegments(url)
    if (!segments.length) return null

    const questIndex = segments.findIndex(segment => segment === 'quest')
    if (questIndex >= 0) {
        const questId = normalizeQuestPathId(segments[questIndex + 1])
        const answerIndex = segments.findIndex(
            (segment, index) => index > questIndex + 1 && segment === 'answer',
        )
        const answerId = answerIndex >= 0 ? segments[answerIndex + 1] : null

        if (answerId) return { type: 'answer', id: answerId, answerId, questId }
        if (questId) return { type: 'quest', id: questId, questId }
    }

    const pIndex = segments.findIndex(segment => segment === 'p')
    const shareId = pIndex >= 0 ? normalizeQuestPathId(segments[pIndex + 1]) : null
    if (shareId) return { type: 'share', id: shareId }

    const anonymousShareIndex = segments.findIndex(segment => segment === 's')
    const anonymousShareId =
        anonymousShareIndex >= 0 ? normalizeQuestPathId(segments[anonymousShareIndex + 1]) : null
    if (anonymousShareId) return { type: 'anonymous_share', id: anonymousShareId }

    const answerIndex = segments.findIndex(segment => segment === 'answer')
    const answerId = answerIndex >= 0 ? segments[answerIndex + 1] : null
    if (answerId) return { type: 'answer', id: answerId, answerId }

    return null
}

export function extractKnovEmbedId(url: string): string | null {
    if (!url || typeof url !== 'string') return null
    const target = extractKnovEmbedTarget(url)
    if (target?.id) return target.id

    try {
        const parsed = new URL(url)
        const path = parsed.pathname.replace(/\/+$/, '')
        const lastSegment = path.split('/').pop()
        if (lastSegment && lastSegment.length === UUID_LENGTH) {
            return lastSegment
        }
        const match = path.match(UUID_REGEX)
        return match ? match[0] : null
    } catch (e) {
        const stripped = url.split('?')[0].split('#')[0].replace(/\/+$/, '')
        const lastSegment = stripped.split('/').pop()
        if (lastSegment && lastSegment.length === UUID_LENGTH) {
            return lastSegment
        }
        const match = stripped.match(UUID_REGEX)
        return match ? match[0] : null
    }
}
